import distance from '@turf/distance';
import { Marker, Popup } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import viewerMarker from '../../assets/images/panoramax-viewer-marker.svg';
import { PegmanControl } from '../../components/map/pegman-control';
import { PanoramaxService } from '../../services';
import { injectStyleOnce } from '../../utils/inject-style';
const sourceId = 'panoramax';
const sequencesLayerId = 'panoramax-sequences';
const picturesLayerId = 'panoramax-pictures';
const pictureDotsLayerId = 'panoramax-picture-dots';
/**
 * Sequences are shaded by how recent they are, darkest first: fresh imagery is what makes the
 * coverage worth browsing, so it gets the most contrast against the basemap.
 *
 * A single-hue ordinal ramp, validated against a light basemap (monotone lightness, ≥ 0.06 gaps,
 * light end at 2.43:1 — above the 2:1 floor).
 */
const ageColors = {
  /** Less than a year old. */
  recent: '#da3a87',
  /** Between one and two years old. */
  fading: '#ff9acb',
  /** Over two years old, and anything with a missing or bogus date. */
  old: '#ffebf5'
};
// Below this the sequences pile up too much to aim at a street.
const minZoom = 12;
// From here the tiles carry individual pictures, which can be hit-tested directly. Below it only
// sequences are available, and the picture has to be looked up through the API.
const picturesMinZoom = PanoramaxService.tilesMaxZoom;
// Radius, in screen pixels, of the area searched around the pointer — for hit-testing the tiles
// as much as for the API lookup further out. Aiming the pegman at a dot on a map is fiddly, so
// the search is deliberately loose and the drop snaps to whatever picture is nearest.
const grabRadius = 24;
// Radius of the invisible circle the pegman is hit-tested against. It adds to `grabRadius`, since
// the query box only has to touch the circle to hit it.
const pictureGrabRadius = 8;
// The visible dot stays well inside the grab area: it marks where the pictures are without
// crowding the sequence, whose colour is what carries the age.
const pictureDotRadius = 2.5;
// Screen distance the map scale is measured over. Long enough not to be swamped by the rounding
// of a single pixel, short enough to stay a local measure.
const scaleProbe = 64;
const pictureDotColor = '#ffffff';
const pictureDotStrokeColor = 'rgba(0, 0, 0, 0.35)';
const panoramaFilter = ['==', ['get', 'type'], 'equirectangular'];
const previewPopupClassName = 'map-panoramax-preview-popup';
// The thumbnail sits in a thin white frame, which is the popup's own background showing through
// its padding. MapLibre's default padding is uneven — 15px top, 10px sides — so it is replaced
// rather than adjusted.
const previewPopupStyle = `.${previewPopupClassName} .maplibregl-popup-content { padding: 2px; }`;

/** `date` is an ISO day, which compares correctly as a plain string. */
function isoYearsAgo(years) {
  const date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date.toISOString().slice(0, 10);
}

/**
 * Colours a sequence by age. The thresholds are resolved when the layer is created rather than
 * baked in, since "a year ago" moves; `coalesce` keeps a missing date from throwing on the
 * comparison, and lands it in the oldest bucket.
 */
function getAgeColor() {
  const date = ['coalesce', ['get', 'date'], ''];
  return ['case', ['>=', date, isoYearsAgo(1)], ageColors.recent, ['>=', date, isoYearsAgo(2)], ageColors.fading, ageColors.old];
}

/** A picture from the tiles, with where it currently sits on screen. */

/**
 * Panoramax street-level imagery browsing: a pegman that toggles the coverage on/off on a plain
 * click, and — when dragged — reveals the coverage, previews the picture under the pointer, and
 * hands the dropped one over to a viewer.
 *
 * Opt-in — nothing is added to the map until `init` is called.
 */
export function usePanoramax(theme, map, _zoom, viewerPosition, {
  onPictureViewed
}) {
  const [initialized, setInitialized] = useState(false);
  const zoom = useRef(_zoom);
  const pegmanControlRef = useRef(new PegmanControl('panoramax', {
    minZoom
  }));
  const previewedPicture = useRef(null);
  const previewPopup = useRef(new Popup({
    className: previewPopupClassName,
    closeButton: false,
    // The thumbnail has a size of its own, and any cap near it clips the frame on the side the
    // rounding falls on rather than shrinking the picture.
    maxWidth: 'none',
    anchor: 'top'
  }));
  const previewContent = useRef(null);
  const viewerMarkerRef = useRef(null);
  const beforeLayerIdRef = useRef(undefined);
  // `initialized` as state is what the caller renders on, but it lags a render behind, so the
  // guards that must not run twice — and `destroy` — read this instead.
  const initializedRef = useRef(false);
  // The exact functions handed to `on`/`addEventListener`: they are rebuilt every render, and
  // only the instance that was registered can be taken back off.
  const listeners = useRef({});
  // A map that has been removed has no style left, and layers and sources are read through it.
  const mapRemoved = useRef(false);
  const locked = useRef(false);
  // Persistent on/off state driven by a plain click on the pegman, independent of the drag
  // gesture — which reveals the layer for its own duration regardless of this toggle.
  const layerActive = useRef(false);
  const lookup = useRef({});
  const {
    t
  } = useTranslation();
  useEffect(() => {
    injectStyleOnce('panoramax-preview-popup-style', previewPopupStyle);
    const previewContentElement = document.createElement('div');
    previewContentElement.style.backgroundColor = 'whitesmoke';
    previewContentElement.style.backgroundPosition = 'center';
    previewContentElement.style.backgroundRepeat = 'no-repeat';
    previewContentElement.style.backgroundSize = 'cover';
    previewContentElement.style.height = '128px';
    previewContentElement.style.width = '256px';
    previewContent.current = previewContentElement;
    const viewerMarkerElement = document.createElement('div');
    viewerMarkerElement.style.backgroundImage = `url(${viewerMarker})`;
    viewerMarkerElement.style.backgroundPosition = 'center';
    viewerMarkerElement.style.backgroundRepeat = 'no-repeat';
    viewerMarkerElement.style.backgroundSize = 'contain';
    viewerMarkerElement.style.width = '32px';
    viewerMarkerElement.style.height = '32px';
    viewerMarkerRef.current = new Marker({
      element: viewerMarkerElement
    });
  }, []);

  // Runs while a picture is open too: zooming the map far out has to put the coverage away, even
  // though the picture — and its marker — stay.
  useEffect(() => {
    if (!initialized || _zoom === undefined) return;
    const wasBrowsable = zoom.current !== undefined && zoom.current >= minZoom;
    const isBrowsable = _zoom >= minZoom;
    zoom.current = _zoom;
    if (wasBrowsable === isBrowsable) return;
    if (isBrowsable) {
      // Back in range: the coverage is only worth showing again while a picture is open or the
      // toggle is on, otherwise it waits for the next drag.
      if (locked.current || layerActive.current) showLayers();
    } else {
      cancelLookup();
      clearPreview();
      showLayers(false);
    }

    // Re-renders the control so it picks up its out-of-range look. `locked`/`active` are left
    // alone: they do not change just because the map was zoomed.
    pegmanControlRef.current.update();
  }, [initialized, _zoom]);
  useEffect(() => {
    if (!map) return;
    mapRemoved.current = false;
    function handleMapRemove() {
      mapRemoved.current = true;
    }
    map.on('remove', handleMapRemove);
    return () => {
      map.off('remove', handleMapRemove);
      destroy();
    };
  }, [map]);
  useEffect(() => {
    if (viewerPosition) {
      if (!map) return;
      const {
        location,
        bearing
      } = viewerPosition;
      map.flyTo({
        center: location,
        zoom: Math.max(17, map.getZoom())
      });
      viewerMarkerRef.current?.setLngLat(location).setRotation(bearing).addTo(map);
    } else {
      unlockPegman();
      viewerMarkerRef.current?.remove();
      showLayers(layerActive.current);
    }
  }, [viewerPosition]);
  function lockPegman() {
    locked.current = true;
    pegmanControlRef.current.lock();
  }
  function unlockPegman() {
    locked.current = false;
    pegmanControlRef.current.unlock();
  }
  function clearPreview() {
    previewedPicture.current = null;
    previewPopup.current.remove();
    if (previewContent.current) previewContent.current.style.backgroundImage = 'none';
  }
  function getSequenceId({
    first_sequence,
    sequences
  }) {
    if (typeof first_sequence === 'string' && first_sequence) return first_sequence;

    // Vector tiles serialize `sequences` as a JSON array of ids.
    if (typeof sequences === 'string') {
      try {
        const parsed = JSON.parse(sequences);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') return parsed[0];
      } catch {
        // malformed: fall through
      }
    }
    return null;
  }
  function toMapPoint({
    clientX,
    clientY
  }) {
    if (!map) return null;

    // Hit-testing takes coordinates relative to the map container, which is not necessarily
    // flush with the top-left of the viewport (header, side panel…).
    const {
      left,
      top
    } = map.getContainer().getBoundingClientRect();
    return [clientX - left, clientY - top];
  }
  function showPreview(picture, point) {
    if (!map) return;
    previewPopup.current.setLngLat(map.unproject(point));
    if (previewedPicture.current?.pictureId !== picture.pictureId) {
      previewedPicture.current = picture;
      if (previewContent.current) {
        previewContent.current.style.backgroundImage = `url(${PanoramaxService.getThumbnailUrl(picture.pictureId)})`;
        previewPopup.current.setDOMContent(previewContent.current);
      }
    }
    if (!previewPopup.current.isOpen()) previewPopup.current.addTo(map);
  }

  /** Everything the pointer's search box touches on `layerId`, in no particular order. */
  function queryAround([x, y], layerId) {
    if (!map?.getLayer(layerId)) return [];
    return map.queryRenderedFeatures([[x - grabRadius, y - grabRadius], [x + grabRadius, y + grabRadius]], {
      layers: [layerId]
    });
  }
  function getCoordinates(feature) {
    if (feature.geometry.type !== 'Point') return null;
    const [lng, lat] = feature.geometry.coordinates;
    return [lng, lat];
  }

  /**
   * The moment a tile picture was taken, as a comparable number.
   *
   * `ts` comes as `YYYY-MM-DD HH:MM:SS[.fff]+00`, where neither the space separator nor the
   * two-digit offset is valid ISO 8601 — both are patched before parsing rather than compared as
   * plain strings, which the optional fractional seconds would get wrong.
   */
  function getTime(feature) {
    const ts = feature.properties?.ts;
    if (typeof ts !== 'string') return 0;
    const time = Date.parse(ts.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'));
    return Number.isNaN(time) ? 0 : time;
  }

  /**
   * How many metres a screen pixel covers around `point`, measured off the map itself so it
   * follows whatever it is currently showing rather than assuming a projection.
   */
  function getMetersPerPixel([x, y]) {
    if (!map) return 0;
    const from = map.unproject([x, y]);
    const to = map.unproject([x + scaleProbe, y]);
    return distance([from.lng, from.lat], [to.lng, to.lat], {
      units: 'meters'
    }) / scaleProbe;
  }

  /**
   * The most recent of the pictures covering the same spot as `anchor` — a street driven again
   * years later leaves several passes on top of each other, and the fresh one is what is worth
   * opening. The others stay reachable from the viewer.
   *
   * The spot is delimited in screen space, from a threshold converted once by the caller: a
   * dense city puts several hundred pictures in the box and this runs on every pointer move, so
   * nothing here may measure a real distance or parse a date per picture.
   */
  function getFreshestAt(candidates, anchor, squaredRadius) {
    let freshest = anchor.feature;
    let freshestTime = getTime(anchor.feature);
    for (const {
      feature,
      x,
      y
    } of candidates) {
      if ((x - anchor.x) ** 2 + (y - anchor.y) ** 2 > squaredRadius) continue;
      const time = getTime(feature);
      if (time <= freshestTime) continue;
      freshest = feature;
      freshestTime = time;
    }
    return freshest;
  }

  /**
   * The picture to open for `point` in the tiles: the one nearest it, then the most recent of
   * those covering that same spot. Only meaningful from `picturesMinZoom` up — below it the
   * `pictures` source-layer is empty and `searchPicture` has to be used instead.
   *
   * One pass, projecting each picture once: the box the query hands back holds hundreds of them
   * in a dense city, and this runs on every pointer move of a pegman drag.
   */
  function getTilePicture(point) {
    if (!map) return null;
    const [pointerX, pointerY] = point;
    const candidates = [];
    let nearest = null;
    for (const feature of queryAround(point, picturesLayerId)) {
      const coordinates = getCoordinates(feature);
      if (!coordinates) continue;
      const {
        x,
        y
      } = map.project(coordinates);
      const squaredDistance = (x - pointerX) ** 2 + (y - pointerY) ** 2;
      candidates.push({
        feature,
        x,
        y
      });
      if (!nearest || squaredDistance < nearest.squaredDistance) nearest = {
        feature,
        x,
        y,
        squaredDistance
      };
    }
    if (!nearest) return null;
    const metersPerPixel = getMetersPerPixel(point);
    // A scale of zero — an unprojectable point — leaves only pictures sharing the anchor's exact
    // position, so the anchor itself is what comes back.
    const squaredRadius = metersPerPixel ? (PanoramaxService.samePlaceRadius / metersPerPixel) ** 2 : 0;
    const feature = getFreshestAt(candidates, nearest, squaredRadius);
    const pictureId = typeof feature.properties?.id === 'string' ? feature.properties.id : null;
    if (!pictureId) return null;

    // Tiles carry everything needed to build the thumbnail URL: no request needed.
    return {
      pictureId,
      sequenceId: getSequenceId(feature.properties)
    };
  }

  /** The picture nearest `point`, asked of the API over the same box the tiles are queried with. */
  function searchPicture(point, signal) {
    if (!map) return Promise.resolve(null);
    const [x, y] = point;
    const min = map.unproject([x - grabRadius, y + grabRadius]);
    const max = map.unproject([x + grabRadius, y - grabRadius]);
    return PanoramaxService.searchPanorama([min.lng, min.lat, max.lng, max.lat], map.unproject(point), {
      signal
    });
  }

  /** Whichever of the two the current zoom makes available, with no preview shown along the way. */
  async function findPicture(point) {
    if (!map) return null;
    if (map.getZoom() >= picturesMinZoom) return getTilePicture(point);

    // Further out the tiles only hold sequences: nothing to search around if none is under the
    // point, and the API call is spared.
    if (!queryAround(point, sequencesLayerId).length) return null;
    return searchPicture(point);
  }
  function handleMove(event) {
    const point = toMapPoint(event);
    if (!map || !point) return;
    if (map.getZoom() >= picturesMinZoom) {
      const picture = getTilePicture(point);
      if (!picture) clearPreview();else showPreview(picture, point);
      return;
    }

    // Further out the tiles only hold sequences, so the picture is looked up from the API.
    if (!queryAround(point, sequencesLayerId).length) {
      cancelLookup();
      clearPreview();
      return;
    }
    scheduleLookup(point);
  }

  /**
   * While a picture is open the map click browses the imagery instead of dropping a way point —
   * the routing form gives the click up for as long as the viewer is up, so nothing competes for
   * it. A click on bare map leaves the open picture alone: closing is the viewer's own job.
   */
  async function handleClick({
    point: {
      x,
      y
    }
  }) {
    if (!locked.current) return;
    const picture = await findPicture([x, y]);

    // The lookup can outlive the viewer being closed, which must not reopen it.
    if (picture && locked.current) onPictureViewed(picture);
  }
  function cancelLookup() {
    window.clearTimeout(lookup.current.timeout);
    lookup.current.controller?.abort();
    lookup.current = {};
  }

  /** Debounced: the pointer sweeps across many sequences on its way to the intended one. */
  function scheduleLookup(point) {
    cancelLookup();
    lookup.current.point = point;
    lookup.current.timeout = window.setTimeout(() => runLookup(point), 200);
  }
  async function runLookup(point) {
    if (!map) return null;
    const controller = new AbortController();
    lookup.current.controller = controller;
    const picture = await searchPicture(point, controller.signal);
    if (controller.signal.aborted) return null;
    if (!picture) {
      clearPreview();
      return null;
    }
    showPreview(picture, point);
    return picture;
  }
  function showLayers(visible = true) {
    if (!map?.getLayer(sequencesLayerId)) return;
    const visibility = visible ? 'visible' : 'none';
    map.setLayoutProperty(sequencesLayerId, 'visibility', visibility).setLayoutProperty(picturesLayerId, 'visibility', visibility).setLayoutProperty(pictureDotsLayerId, 'visibility', visibility);
    if (visible) moveUnderneath();
  }

  /**
   * Keeps the coverage below the route.
   *
   * The ordering cannot be settled once at init: the `routes` layer is only added once an
   * itinerary is computed, which is usually well after the map — and the coverage — came up.
   */
  function moveUnderneath() {
    const beforeLayerId = beforeLayerIdRef.current;
    if (!map || !beforeLayerId || !map.getLayer(beforeLayerId)) return;
    map.moveLayer(sequencesLayerId, beforeLayerId).moveLayer(picturesLayerId, beforeLayerId).moveLayer(pictureDotsLayerId, beforeLayerId);
  }
  function onDragStart() {
    showLayers();
    listeners.current.move = handleMove;
    map?.getContainer().addEventListener('pointermove', handleMove);
  }

  /** Resolves to whether a picture was found at the drop point — the control bounces the pegman
   * back into its button when it wasn't. */
  async function onDragEnd() {
    stopTracking();

    // Dropped before the debounced lookup had a chance to run: resolve it now rather than
    // silently doing nothing.
    const pendingPoint = lookup.current.point;
    if (!previewedPicture.current && pendingPoint) {
      window.clearTimeout(lookup.current.timeout);
      await runLookup(pendingPoint);
    }
    const picture = previewedPicture.current;
    if (picture) {
      // Dropped on a picture — opens it, whether or not one was already being viewed.
      showLayers();
      onPictureViewed(picture);
      lockPegman();
    } else if (locked.current) {
      // Dropped on nothing while viewing: leave the picture alone. Closing is the viewer's own
      // job, through its close button.
      showLayers();
    } else {
      // Back to whatever the toggle says, rather than hard off — the drag was just a detour.
      showLayers(layerActive.current);
      unlockPegman();
    }
    cancelLookup();
    clearPreview();
    return picture !== null;
  }
  function toggleLayer() {
    layerActive.current = !layerActive.current;
    showLayers(layerActive.current);
    pegmanControlRef.current.setActive(layerActive.current);
  }
  function init({
    beforeLayerId
  } = {}) {
    if (!map || initializedRef.current) return;

    // Optional, and usually not there yet: `showLayers` re-applies the ordering later on.
    beforeLayerIdRef.current = beforeLayerId;
    const beforeId = beforeLayerId && map.getLayer(beforeLayerId) ? beforeLayerId : undefined;
    map.addSource(sourceId, {
      type: 'vector',
      tiles: [PanoramaxService.getTilesUrl()],
      minzoom: 0,
      maxzoom: PanoramaxService.tilesMaxZoom
    });
    map.addLayer({
      id: sequencesLayerId,
      type: 'line',
      source: sourceId,
      'source-layer': 'sequences',
      // Also stops the `.mvt` requests below it: MapLibre only fetches tiles for the zoom
      // range its layers actually cover.
      minzoom: minZoom,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'none'
      },
      paint: {
        'line-opacity': 0.7,
        'line-color': getAgeColor(),
        'line-width': 4
      },
      filter: panoramaFilter
    }, beforeId);

    // Pictures come as two layers, because what is comfortable to grab is too big to look at.
    // This one is fully transparent and is the only one the pegman is hit-tested against:
    // `queryRenderedFeatures` ignores opacity but skips hidden layers.
    map.addLayer({
      id: picturesLayerId,
      type: 'circle',
      source: sourceId,
      'source-layer': 'pictures',
      minzoom: minZoom,
      layout: {
        visibility: 'none'
      },
      paint: {
        'circle-opacity': 0,
        'circle-radius': pictureGrabRadius
      },
      filter: panoramaFilter
    }, beforeId);

    // …and this one is the small dot that shows where the pictures are. Purely decorative: it is
    // never queried, so its size is free to stay legible rather than aimable.
    map.addLayer({
      id: pictureDotsLayerId,
      type: 'circle',
      source: sourceId,
      'source-layer': 'pictures',
      // Below this the tiles only carry sequences, so there is nothing to dot.
      minzoom: picturesMinZoom,
      layout: {
        visibility: 'none'
      },
      paint: {
        'circle-radius': pictureDotRadius,
        'circle-color': pictureDotColor,
        'circle-stroke-width': 1,
        'circle-stroke-color': pictureDotStrokeColor
      },
      filter: panoramaFilter
    }, beforeId);

    // Only acts while a picture is open, so it costs nothing the rest of the time.
    listeners.current.click = handleClick;
    map.on('click', handleClick);
    map.addControl(pegmanControlRef.current, 'bottom-right');
    pegmanControlRef.current.init(theme, {
      tooFar: t('commons.panoramax.actions.zoom_more'),
      locked: t('commons.panoramax.actions.move_pegman'),
      unlocked: t('commons.panoramax.actions.show_layers')
    }, {
      onDragStart,
      onDragEnd,
      onToggle: toggleLayer
    });
    initializedRef.current = true;
    setInitialized(true);
  }

  /** Stops following the pointer, whichever render registered the listener. */
  function stopTracking() {
    const move = listeners.current.move;
    if (!move) return;
    listeners.current.move = undefined;
    map?.getContainer().removeEventListener('pointermove', move);
  }

  /**
   * Takes back off the map everything this hook put on it.
   *
   * The map outlives the component in both apps — they hold it in their own state — so nothing
   * can be left to garbage collection: a marker left behind stays on screen next to the one the
   * next mount adds, and a source left behind makes `init` throw on the way back in.
   */
  function destroy() {
    if (!map) return;
    cancelLookup();
    clearPreview();
    stopTracking();
    viewerMarkerRef.current?.remove();
    if (!initializedRef.current) return;
    initializedRef.current = false;
    setInitialized(false);
    locked.current = false;
    layerActive.current = false;
    const click = listeners.current.click;
    if (click) {
      listeners.current.click = undefined;
      map.off('click', click);
    }

    // A removed map took its own style down with it — reading a layer off it now would throw,
    // and its controls are already unmounted.
    if (mapRemoved.current) return;
    if (map.hasControl(pegmanControlRef.current)) map.removeControl(pegmanControlRef.current);
    for (const layerId of [pictureDotsLayerId, picturesLayerId, sequencesLayerId]) if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  }
  return {
    initialized,
    init,
    destroy
  };
}